import React from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { formatDate } from '@lib/utils.ts';

interface TradeData {
  date: string;
  abandonment: number;
  clickThrough: number;
  values?: string;
}

type TradePayload = {
  value: number;
  values?: string;
  payload?: TradeData;
  color?: string;
  dataKey?: string;
};

interface TooltipProps {
  active?: boolean;
  payload?: TradePayload[];
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #DC342E14',
        }}
      >
        <p className={'text-[#DC342E] text-xl'}>
          {payload[0]?.payload?.abandonment}
        </p>
        <p className={'text-[#255589] text-xl'}>
          {payload[0]?.payload?.clickThrough}
        </p>
        <p className={'text-[#999999] text-[10px]'}>
          {formatDate(payload[0]?.payload?.date)}
        </p>
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  payload?: TradePayload[];
}

const CustomLegend: React.FC<CustomLegendProps> = ({ payload }) => {
  if (!payload) return null;

  return (
    <div className={'flex justify-between items-center mb-5'}>
      <p className={'text-[22px] font-semibold'}>Cart Activity</p>
      <div className={'flex gap-x-6 p-2.5'}>
        {payload.map((entry, index) => (
          <div key={index} className={'flex items-center gap-x-1.5'}>
            <div
              className={`w-3 h-3 rounded-full`}
              style={{
                backgroundColor:
                  entry.dataKey === 'abandonment' ? '#DC342E' : '#255589',
              }}
            />
            <p>{entry?.payload?.values}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface CartActivityProps {
  data: TradeData[];
}

export const CartActivity: React.FC<CartActivityProps> = ({ data }) => {
  return (
    <div className={'flex w-full flex-col'}>
      <ResponsiveContainer height={600} width="100%">
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'transparent' }}
          />
          <Legend
            align="center"
            content={<CustomLegend />}
            layout="horizontal"
            verticalAlign="top"
          />
          <CartesianGrid
            stroke="#919EAB3D"
            strokeDasharray="2 2"
            vertical={false}
          />
          <XAxis
            axisLine={false}
            dataKey="date"
            fontSize={12}
            padding={{ left: 0, right: 0 }}
            stroke="#919EAB"
            tickFormatter={(date) => format(parseISO(date), 'MMM')}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            domain={[0, 100]}
            fontSize={12}
            stroke="#919EAB"
            tickLine={false}
            yAxisId="left"
          />
          <defs>
            <linearGradient id="barGradientBlue" x1="0" x2="0" y1="1" y2="0">
              <stop offset="0%" stopColor="rgba(37, 85, 137, 0)" />
              <stop offset="100%" stopColor="#035DC6" />
            </linearGradient>
            <linearGradient id="barGradientRed" x1="0" x2="0" y1="1" y2="0">
              <stop offset="0%" stopColor="rgba(220, 52, 46, 0)" />
              <stop offset="100%" stopColor="#910500" />
            </linearGradient>
          </defs>
          <Bar
            barSize={19}
            dataKey="abandonment"
            fill="url(#barGradientRed)"
            values={'Abandonment'}
            yAxisId="left"
          />
          <Bar
            barSize={19}
            dataKey="clickThrough"
            fill="url(#barGradientBlue)"
            values={'Clickthrough'}
            yAxisId="left"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
